import {
  collection,
  addDoc,
  Timestamp,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  DocumentReference,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { COLLECTIONS } from "constant";
import { CartItem, Purchase } from "../types/types";
import { db, storage } from "@firebase";
import { ref } from "firebase/storage";

export class PurchasesService {
  static async createPurchase(
    purchaseData: Omit<Purchase, "id" | "createdAt" | "updatedAt">
  ): Promise<DocumentReference> {
    try {
      const purchasesCollection = collection(db, COLLECTIONS.PURCHASES);

      // Generate a document reference with an ID
      const newDocRef = doc(purchasesCollection);
      const newId = newDocRef.id; // Extract the generated ID
      console.log("newId", newId);

      // Retrieve the content document to get the creatorId
      const contentDocRef = doc(db, `contents/${purchaseData.contentId}`);
      const contentDoc = await getDoc(contentDocRef);

      if (!contentDoc.exists()) {
        throw new Error(
          `Content with ID ${purchaseData.contentId} does not exist.`
        );
      }

      const contentData = contentDoc.data();
      const creatorId = contentData?.createdBy?.id;

      if (!creatorId) {
        throw new Error(
          `Creator ID is missing in the content with ID ${purchaseData.contentId}`
        );
      }

      // Create the purchase object without the ID field initially
      const newPurchase = {
        ...purchaseData,
        id: newId, // Add the `id` field here
        creatorId: doc(db, `users/${creatorId}`), // Convert to DocumentReference
        // contentId: doc(db, `contents/${purchaseData.contentId}`), // Convert to DocumentReference
        contentId: contentDocRef, // Use DocumentReference for contentId
        currentStatus: "PENDING",
        statusHistory: [{ status: "PENDING", date: Timestamp.now() }],
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      console.log("Adding purchase to Firestore:", newPurchase);

      // Save the document with the `id` field included
      await setDoc(newDocRef, newPurchase);

      console.log(
        "Purchase created successfully with matching ID:",
        newDocRef.id
      );

      return newDocRef; // Return the document reference
    } catch (error) {
      console.error("Error creating purchase:", error);
      throw error;
    }
  }

  static async getPurchases(purchaseId: string) {
    try {
      const purchaseRef = doc(db, COLLECTIONS.PURCHASES, purchaseId);
      const purchaseDoc = await getDoc(purchaseRef);

      if (!purchaseDoc.exists()) {
        console.warn(`No purchase found with ID: ${purchaseId}`);
        return null;
      }

      console.log("Fetched purchase:", purchaseDoc.data());
      return { id: purchaseDoc.id, ...purchaseDoc.data() };
    } catch (error) {
      console.error("Error fetching purchase by ID:", error);
      throw error;
    }
  }

  static async getPurchasesByUser(userId: string): Promise<Purchase[]> {
    try {
      const purchasesCollection = query(
        collection(db, COLLECTIONS.PURCHASES),
        where("userId", "==", userId)
      );

      const snapshot = await getDocs(purchasesCollection);

      if (snapshot.empty) {
        console.warn("No purchases found for user");
        return [];
      }

      const purchases: Purchase[] = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const purchaseData = doc.data() as Purchase;

          let cartItemData = [] as any[];

          // Fetch cart items if cartId exists
          if (purchaseData.cartId) {
            try {
              const cartId =
                typeof purchaseData.cartId === "string"
                  ? purchaseData.cartId
                  : (purchaseData.cartId as DocumentReference).id;

              const cartItemsCollection = collection(
                db,
                COLLECTIONS.CART_ITEMS
              );
              const cartItemsSnapshot = await getDocs(cartItemsCollection);

              cartItemData = await Promise.all(
                cartItemsSnapshot.docs
                  .map((cartDoc) => {
                    const data = cartDoc.data() as any;
                    const normalizedCartId =
                      typeof data.cartId === "string"
                        ? data.cartId
                        : (data.cartId as DocumentReference)?.id;

                    return {
                      id: cartDoc.id,
                      ...data,
                      cartId: normalizedCartId,
                    };
                  })
                  .filter((cartItem) => cartItem.cartId === cartId)
                  .map(async (cartItem) => {
                    let contentData = null;
                    let createdByData = null;

                    // Fetch content data if contentId exists
                    if (
                      cartItem.contentId &&
                      cartItem.contentId instanceof DocumentReference
                    ) {
                      const contentDoc = await getDoc(cartItem.contentId);
                      if (contentDoc.exists()) {
                        contentData = contentDoc.data() as {
                          createdBy?: DocumentReference;
                        };

                        // Fetch `createdBy` data if available
                        if (
                          contentData?.createdBy instanceof DocumentReference
                        ) {
                          const createdByDoc = await getDoc(
                            contentData.createdBy
                          );
                          if (createdByDoc.exists()) {
                            createdByData = createdByDoc.data();
                          }
                        }
                      }
                    }

                    return {
                      ...cartItem,
                      contentData: {
                        ...contentData,
                        createdBy: createdByData, // Attach `createdBy` user data
                      },
                    };
                  })
              );
            } catch (error) {
              console.error("Error fetching cart items:", error);
            }
          }

          return {
            ...purchaseData,
            cartItemData,
          };
        })
      );

      return purchases;
    } catch (error) {
      console.error("Error fetching purchases by user:", error);
      throw error;
    }
  }

  static async getPurchasesed(purchaseId: string) {
    try {
      const purchaseRef = doc(db, COLLECTIONS.PURCHASES, purchaseId);
      const purchaseDoc = await getDoc(purchaseRef);

      if (!purchaseDoc.exists()) {
        console.warn(`No purchase found with ID: ${purchaseId}`);
        return null;
      }

      const purchaseData = purchaseDoc.data() as Purchase;

      // Resolve `contentData`
      let contentData = null;
      if (purchaseData.contentId instanceof DocumentReference) {
        const contentDoc = await getDoc(purchaseData.contentId);
        if (contentDoc.exists()) {
          contentData = { id: contentDoc.id, ...contentDoc.data() } as {
            id: string;
            createdBy?: DocumentReference;
          };
        } else {
          console.warn(
            `No content found for contentId: ${purchaseData.contentId.id}`
          );
        }
      }

      // Debug `createdBy` before resolving

      // Resolve `createdBy` within `contentData`
      if (contentData?.createdBy instanceof DocumentReference) {
        const userDoc = await getDoc(contentData.createdBy);

        if (userDoc.exists()) {
          //@ts-ignore
          contentData.createdBy = {
            id: userDoc.id,
            ...userDoc.data(),
          }; // Retain DocumentReference
        } else {
          console.warn(
            `No user found for createdBy: ${contentData.createdBy.id}`
          );
        }
      }

      return { ...purchaseData, contentData, id: purchaseDoc.id };
    } catch (error) {
      console.error("Error fetching purchase by ID:", error);
      throw error;
    }
  }

  // Fetch all purchases by the logged-in user's creatorId
  static async getPurchasesByCreatorId(creatorId: string): Promise<Purchase[]> {
    try {
      const purchasesCollection = query(
        collection(db, COLLECTIONS.PURCHASES),
        where("creatorId", "==", doc(db, COLLECTIONS.USER, creatorId)) // Match creatorId
      );

      const snapshot = await getDocs(purchasesCollection);

      if (snapshot.empty) {
        console.warn("No purchases found for creator");
        return [];
      }

      // Map purchase data
      const purchases: Purchase[] = snapshot.docs.map((doc) => {
        const data = doc.data() as Purchase;
        return { ...data, id: doc.id }; // Ensure `id` is added last to avoid conflicts
      });

      return purchases;
    } catch (error) {
      console.error("Error fetching purchases by creatorId:", error);
      throw error;
    }
  }
}
